import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, Upload, X, FileText } from 'lucide-react';

const OPTIONS = {
  region: ['Adamaoua','Centre','Est','Extrême-Nord','Littoral','Nord','Nord-Ouest','Ouest','Sud','Sud-Ouest'],
  unit: ['Brigade Anti-Criminalité','Brigade de Recherches','Compagnie de Sécurisation','Direction Centrale de la Police Judiciaire','Direction de la Sécurité Publique','Direction de la Surveillance du Territoire','Groupement Mobile d\'Intervention','Police de l\'Air et des Frontières','Police des Frontières','Unité Spéciale d\'Intervention'],
  budget_line: ['Achats Spéciaux','Carburant et Lubrifiants','Équipements de Sécurité','Formation et Renforcement des Capacités','Frais Opérationnels','Infrastructure et Bâtiments','Maintenance des Véhicules','Personnel et Soldes','Technologie et Systèmes d\'Information'],
  rank: ['Gardien de la Paix','Inspecteur de Police','Officier de Police','Commissaire de Police','Commissaire Divisionnaire'],
  asset_type: ['Ambulance de brigade','Camionnette de transport','Fourgon blindé','Moto de liaison','Véhicule de commandement','Véhicule de patrouille','Véhicule tout-terrain'],
  program_name: ['Actualisation des procédures d\'enquête — CIAP - Mutengene','Analyse du renseignement — CIAP - Mutengene','Analyse du renseignement — ENSP - Yaoundé','Communication et gestion des tensions — CIAP - Mutengene','Communication et gestion des tensions — ENSP - Yaoundé','Criminalistique et médecine légale — CIAP - Mutengene','Criminalistique et médecine légale — ENSP - Yaoundé','Droits fondamentaux en contexte policier — CIAP - Mutengene','Droits fondamentaux en contexte policier — ENSP - Yaoundé','Enquête numérique et cybersécurité — CIAP - Mutengene','Enquête numérique et cybersécurité — ENSP - Yaoundé','Éthique et déontologie policière — CIAP - Mutengene','Éthique et déontologie policière — ENSP - Yaoundé','Formation de base CIAP — CIAP - Mutengene','Formation initiale des Gardiens de la Paix — CIAP - Mutengene','Formation initiale des Gardiens de la Paix — ENSP - Yaoundé','Formation initiale des Inspecteurs — CIAP - Mutengene','Formation initiale des Inspecteurs — ENSP - Yaoundé','Gestion des crises et négociation — CIAP - Mutengene','Gestion des crises et négociation — ENSP - Yaoundé','Gestion des foules et maintien de l\'ordre — CIAP - Mutengene','Gestion des foules et maintien de l\'ordre — ENSP - Yaoundé','Investigation criminelle avancée — CIAP - Mutengene','Investigation criminelle avancée — ENSP - Yaoundé','Lutte contre la criminalité en ligne — CIAP - Mutengene','Lutte contre la criminalité en ligne — ENSP - Yaoundé','Police de proximité et relations communautaires — CIAP - Mutengene','Police de proximité et relations communautaires — ENSP - Yaoundé','Recyclage annuel en techniques d\'intervention — CIAP - Mutengene','Recyclage annuel en techniques d\'intervention — ENSP - Yaoundé','Remise à niveau opérationnelle — CIAP - Mutengene','Remise à niveau opérationnelle — ENSP - Yaoundé','Techniques d\'assaut et de protection rapprochée — CIAP - Mutengene','Techniques d\'assaut et de protection rapprochée — ENSP - Yaoundé'],
  program_type: ['Formation en cybercriminalité','Formation en droits de l\'homme','Formation en médiation communautaire','Formation initiale','Formation spécialisée','Formation tactique avancée','Recyclage opérationnel'],
  process_name: ['Archivage des dossiers judiciaires','Contrôle des identités','Délivrance de laissez-passer','Délivrance des certificats de résidence','Gestion de la paie du personnel','Gestion des gardes à vue','Processus de recrutement','Rédaction des procès-verbaux','Traitement des demandes de visa','Traitement des plaintes','Transmission des rapports d\'enquête'],
  boolean: ['FALSE','TRUE'],
};

export const FIELDS: Record<string, any[]> = {
  finance: [
    { name: 'transaction_id',    type: 'text'   },
    { name: 'transaction_date',  type: 'date'   },
    { name: 'region',            type: 'select', options: 'region'      },
    { name: 'unit',              type: 'select', options: 'unit'        },
    { name: 'budget_line',       type: 'select', options: 'budget_line' },
    { name: 'allocated_amount',  type: 'number' },
    { name: 'committed_amount',  type: 'number' },
    { name: 'disbursed_amount',  type: 'number' },
    { name: 'is_fraud_flagged',  type: 'select', options: 'boolean'     },
    { name: 'processing_days',   type: 'number' },
  ],
  hr: [
    { name: 'employee_id',               type: 'text'   },
    { name: 'matricule',                 type: 'text'   },
    { name: 'full_name',                 type: 'text'   },
    { name: 'region',                    type: 'select', options: 'region' },
    { name: 'unit',                      type: 'select', options: 'unit'   },
    { name: 'rank',                      type: 'select', options: 'rank'   },
    { name: 'base_salary',               type: 'number' },
    { name: 'present_verified',          type: 'select', options: 'boolean' },
    { name: 'assignment_verified',       type: 'select', options: 'boolean' },
    { name: 'training_hours',            type: 'number' },
    { name: 'performance_score',         type: 'number' },
    { name: 'attendance_rate',           type: 'number' },
  ],
  logistique: [
    { name: 'asset_id',                  type: 'text'   },
    { name: 'asset_type',                type: 'select', options: 'asset_type' },
    { name: 'vehicle_registration',      type: 'text'   },
    { name: 'region',                    type: 'select', options: 'region'     },
    { name: 'unit',                      type: 'select', options: 'unit'       },
    { name: 'purchase_date',             type: 'date'   },
    { name: 'purchase_cost',             type: 'number' },
    { name: 'current_mileage',           type: 'number' },
    { name: 'last_maintenance_date',     type: 'date'   },
    { name: 'fuel_consumption_liters',   type: 'number' },
    { name: 'fuel_expected_liters',      type: 'number' },
    { name: 'operational_hours',         type: 'number' },
    { name: 'maintenance_cost_ytd',      type: 'number' },
    { name: 'engine_temp_warning',       type: 'select', options: 'boolean' },
    { name: 'oil_pressure_warning',      type: 'select', options: 'boolean' },
    { name: 'brake_wear_pct',            type: 'number' },
  ],
  formation: [
    { name: 'training_id',                     type: 'text'   },
    { name: 'program_name',                    type: 'select', options: 'program_name' },
    { name: 'region',                          type: 'select', options: 'region'       },
    { name: 'program_type',                    type: 'select', options: 'program_type' },
    { name: 'start_date',                      type: 'date'   },
    { name: 'end_date',                        type: 'date'   },
    { name: 'total_cost',                      type: 'number' },
    { name: 'participants_count',              type: 'number' },
    { name: 'completions_count',               type: 'number' },
    { name: 'pre_training_score',              type: 'number' },
    { name: 'post_training_score',             type: 'number' },
    { name: 'field_performance_improvement',   type: 'number' },
  ],
  lean: [
    { name: 'improvement_id',          type: 'text'   },
    { name: 'region',                  type: 'select', options: 'region'       },
    { name: 'process_name',            type: 'select', options: 'process_name' },
    { name: 'baseline_time_hours',     type: 'number' },
    { name: 'improved_time_hours',     type: 'number' },
    { name: 'baseline_cost_fcfa',      type: 'number' },
    { name: 'improved_cost_fcfa',      type: 'number' },
    { name: 'implementation_date',     type: 'date'   },
    { name: 'savings_realized_fcfa',   type: 'number' },
    { name: 'quality_impact_score',    type: 'number' },
    { name: 'adopted_by_other_units',  type: 'text'   },
  ],
};

interface Props {
  moduleId: 'finance' | 'hr' | 'logistique' | 'formation' | 'lean';
  user: any;
}

export const ModuleForm: React.FC<Props> = ({ moduleId, user }) => {
  const { t } = useTranslation();
  const fields = FIELDS[moduleId] || [];
  const [formData, setFormData]   = useState<Record<string,string>>({});
  const [files, setFiles]         = useState<Record<string,File>>({});
  const [previews, setPreviews]   = useState<Record<string,string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    const init: Record<string,string> = {};
    fields.forEach(f => { init[f.name] = ''; });
    setFormData(init);
  }, [moduleId]);

  const set = (k: string, v: string) => setFormData(d => ({...d, [k]: v}));

  const handleFileChange = (name: string, file: File) => {
    if (file.size > 10 * 1024 * 1024) { alert('Le fichier ne doit pas dépasser 10MB'); return; }
    setFiles(f => ({...f, [name]: file}));
    setPreviews(p => ({...p, [name]: URL.createObjectURL(file)}));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const uploadedUrls: Record<string,string> = {};
      for (const [name, file] of Object.entries(files)) {
        const fd = new FormData(); fd.append('file', file);
        const res = await fetch('/api/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: fd,
        });
        const data = await res.json();
        uploadedUrls[name] = data.url;
      }
      await fetch('/api/forms/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ formType: moduleId, module: moduleId, formData: { ...formData, ...uploadedUrls } }),
      });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
      const init: Record<string,string> = {};
      fields.forEach(f => { init[f.name] = ''; });
      setFormData(init); setFiles({});
      Object.values(previews).forEach(u => URL.revokeObjectURL(u));
      setPreviews({});
    } catch { setError(t('common.error')); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="glass rounded-xl overflow-hidden">
      <div className="p-4 border-b border-navy-700 bg-navy-800/50 flex items-center gap-3">
        <div className="p-2 bg-gold-500/10 rounded-lg border border-gold-500/20">
          <FileText className="w-5 h-5 text-gold-500" />
        </div>
        <div>
          <h2 className="text-white font-semibold">{t('forms.all_fields_required')}</h2>
          <p className="text-xs text-gray-400">Soumis par {user?.name || user?.username} · {user?.role}</p>
        </div>
      </div>
      <div className="p-6">
        {submitted && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 text-green-400 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4" /> {t('forms.success')}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm">{error}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {fields.map(field => {
              const opts = field.options ? OPTIONS[field.options] : null;
              const isFile = field.type === 'file';
              const isTextarea = field.type === 'textarea';
              const label = t(`forms.fields.${field.name}`, { defaultValue: field.name });
              return (
                <div key={field.name} className={isFile || isTextarea ? 'md:col-span-2' : ''}>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">{label} *</label>
                  {isFile ? (
                    <div className="border-2 border-dashed border-navy-600 rounded-lg p-5 text-center">
                      {previews[field.name] ? (
                        <div className="relative inline-block">
                          <img src={previews[field.name]} alt="preview" className="max-h-40 rounded-lg" />
                          <button type="button" onClick={() => { setFiles(f => { const n={...f}; delete n[field.name]; return n; }); setPreviews(p => { const n={...p}; delete n[field.name]; return n; }); }}
                            className="absolute top-1 right-1 p-1 bg-red-500 rounded-full"><X className="w-3 h-3"/></button>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-7 h-7 text-gray-500 mx-auto mb-2"/>
                          <p className="text-xs text-gray-400 mb-2">PNG, JPEG, WEBP · max 10MB</p>
                          <input type="file" accept={field.accept || 'image/*'} id={`file-${field.name}`}
                            onChange={e => e.target.files?.[0] && handleFileChange(field.name, e.target.files[0])} className="hidden"/>
                          <label htmlFor={`file-${field.name}`} className="px-3 py-1.5 bg-navy-700 hover:bg-navy-600 rounded-lg cursor-pointer text-xs text-gray-300 transition-colors">Sélectionner</label>
                        </>
                      )}
                    </div>
                  ) : isTextarea ? (
                    <textarea value={formData[field.name] || ''} onChange={e => set(field.name, e.target.value)} required rows={3}
                      className="w-full px-3 py-2 bg-black/30 border border-navy-600 rounded-lg text-white text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20 resize-none"/>
                  ) : opts ? (
                    <select value={formData[field.name] || ''} onChange={e => set(field.name, e.target.value)} required
                      className="w-full px-3 py-2 bg-black/30 border border-navy-600 rounded-lg text-white text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20">
                      <option value="">{t('forms.select')}</option>
                      {opts.map((o:string) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={field.type} value={formData[field.name] || ''} onChange={e => set(field.name, e.target.value)} required
                      className="w-full px-3 py-2 bg-black/30 border border-navy-600 rounded-lg text-white text-sm focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/20"/>
                  )}
                </div>
              );
            })}
          </div>
          <button type="submit" disabled={submitting}
            className="w-full py-2.5 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-navy-900 font-bold text-sm rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin"/> {t('forms.submitting')}</> : t('forms.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};
